/**
 * Term.tsx — PPP Stream C, DLD §C2
 *
 * Wraps a domain abbreviation with a dotted underline and a Radix tooltip
 * that shows the definition from the "tooltip" i18n namespace.
 *
 * Usage:
 *   <Term term="agm">AGM</Term>
 *   <Term term="eor">EOR licence</Term>
 *
 * If the term key is missing from tooltip.json the component renders children
 * unchanged (graceful no-op). This prevents hard failures from missing keys.
 */

import { useTranslation } from "react-i18next";
import { Tooltip } from "./Tooltip";

interface TermProps {
  /** Key into tooltip.json, e.g. "agm", "ubo", "fye". */
  term: string;
  children: React.ReactNode;
}

export function Term({ term, children }: TermProps): JSX.Element {
  const { t } = useTranslation("tooltip");
  // i18next returns the key itself when missing; treat that as "no definition".
  const definition = t(term, "");
  if (!definition || definition === term) return <>{children}</>;

  return (
    <Tooltip content={definition}>
      <abbr
        title={definition}
        className="cursor-help underline decoration-dotted decoration-text-muted underline-offset-2 no-underline-on-abbr"
        style={{ textDecorationStyle: "dotted" }}
      >
        {children}
      </abbr>
    </Tooltip>
  );
}
